/**
 * Anchored tooltip with step counter, prev/next/skip and an arrow.
 *
 * Always position: fixed (works inside overflow containers). Clamps to
 * viewport so it never disappears off-screen on a 375 px width.
 */
import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { useOnboarding } from './OnboardingContext';
import type { StepDef, TooltipPlacement } from './types';

const TOOLTIP_W = 280;
const OFFSET = 12;

interface Pos { top: number; left: number; arrow: TooltipPlacement }

function computePosition(
  target: DOMRect, tooltip: DOMRect, placement: TooltipPlacement,
): Pos {
  let top = 0; let left = 0; let arrow: TooltipPlacement = placement;
  switch (placement) {
    case 'bottom':
      top = target.bottom + OFFSET;
      left = target.left + target.width / 2 - tooltip.width / 2;
      arrow = 'top'; break;
    case 'top':
      top = target.top - tooltip.height - OFFSET;
      left = target.left + target.width / 2 - tooltip.width / 2;
      arrow = 'bottom'; break;
    case 'right':
      top = target.top + target.height / 2 - tooltip.height / 2;
      left = target.right + OFFSET;
      arrow = 'left'; break;
    case 'left':
      top = target.top + target.height / 2 - tooltip.height / 2;
      left = target.left - tooltip.width - OFFSET;
      arrow = 'right'; break;
  }
  const vw = window.innerWidth; const vh = window.innerHeight;
  left = Math.max(8, Math.min(left, vw - tooltip.width - 8));
  top = Math.max(8, Math.min(top, vh - tooltip.height - 8));
  return { top, left, arrow };
}

export function TourTooltip({
  step, targetEl,
}: { step: StepDef; targetEl: HTMLElement | null }): JSX.Element {
  const { currentIndex, totalSteps, goNext, goPrev, dismiss } = useOnboarding();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<Pos | null>(null);

  useLayoutEffect(() => {
    if (!targetEl || !tooltipRef.current) return;
    const measure = () => {
      const tr = targetEl.getBoundingClientRect();
      const br = tooltipRef.current!.getBoundingClientRect();
      setPos(computePosition(tr, br, step.placement ?? 'bottom'));
    };
    measure();
    window.addEventListener('scroll', measure, { passive: true, capture: true });
    window.addEventListener('resize', measure, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(document.body);
    return () => {
      window.removeEventListener('scroll', measure, { capture: true });
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [targetEl, step.placement]);

  const arrowClasses: Record<TooltipPlacement, string> = {
    top:    'bottom-full left-1/2 -translate-x-1/2 border-x-transparent border-t-transparent',
    bottom: 'top-full    left-1/2 -translate-x-1/2 border-x-transparent border-b-transparent',
    left:   'right-full  top-1/2  -translate-y-1/2 border-y-transparent border-l-transparent',
    right:  'left-full   top-1/2  -translate-y-1/2 border-y-transparent border-r-transparent',
  };
  // Side that "points back" gets the surface color so the arrow looks
  // like an extension of the tooltip body. Tailwind's borderColor-arbitrary
  // doesn't see CSS variables cleanly, so we drive it via inline style below.
  const arrowSurfaceSide: Record<TooltipPlacement, React.CSSProperties> = {
    top:    { borderBottomColor: 'var(--surface, #fff)' },
    bottom: { borderTopColor:    'var(--surface, #fff)' },
    left:   { borderRightColor:  'var(--surface, #fff)' },
    right:  { borderLeftColor:   'var(--surface, #fff)' },
  };

  return createPortal(
    <div
      ref={tooltipRef}
      role="dialog" aria-modal="false" aria-label={step.title}
      style={{
        position: 'fixed',
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        width: TOOLTIP_W,
        zIndex: 9999,
        opacity: pos ? 1 : 0,
        transition: 'opacity 150ms ease',
      }}
      className={cn('rounded-xl border border-line bg-surface shadow-elev-3 p-4 flex flex-col gap-3')}
    >
      {pos && (
        <span
          aria-hidden
          className={cn('absolute w-0 h-0 border-8 border-solid', arrowClasses[pos.arrow])}
          style={arrowSurfaceSide[pos.arrow]}
        />
      )}
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">
          {currentIndex + 1} / {totalSteps}
        </span>
        <button
          type="button" onClick={dismiss}
          className="text-xs text-ink-subtle hover:text-ink"
          aria-label="Fechar tour"
        >✕</button>
      </div>
      <div>
        <p className="mb-1 text-sm font-semibold text-ink">{step.title}</p>
        <p className="text-sm leading-relaxed text-ink-muted">{step.body}</p>
      </div>
      <div className="flex items-center gap-2">
        {currentIndex > 0 && (
          <button type="button" onClick={goPrev} className="text-xs text-ink-muted hover:text-ink">
            ← Voltar
          </button>
        )}
        <div className="flex-1" />
        <Button size="sm" variant="primary" onClick={goNext}>
          {step.ctaLabel ?? 'Próximo →'}
        </Button>
      </div>
    </div>,
    document.body,
  );
}
